import { gql } from '@apollo/client';
import { captureException } from '@sentry/browser';
import React, { FunctionComponent } from 'react';
import { CauseMiniFragment } from '../../../types/__generated__';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';
import { filterByTypename } from '../../utils/graphql';
import { getCampaigns } from '../../utils/relationships';
import AboutCampaignMini from '../AboutCampaignMini/AboutCampaignMini';
import AboutCharityMini from '../AboutCharityMini/AboutCharityMini';
import AboutRelationshipMiniSkeleton from '../AboutRelationship/AboutRelationshipSkeleton';

// Beware not all fields are available for old campaigns
// requesting them in 'parent' field
// results in page data being null for requested campaign

const FRAGMENT = buildQuery(gql`
  fragment CauseMini on Page {
    id
    relationships {
      beneficiaries(first: 6) {
        nodes {
          ... on Charity {
            impactStatement
            id
            legacyId
            logo
            name
            profilePageUrl
            registrationNumber
            slug
            countryCode
          }
        }
        totalCount
      }
      parents {
        type
        page {
          id
          url
          logo
          title
          summary
          branding {
            logo
          }
          product {
            name
          }
          type
        }
      }
    }
  }
`);

const AboutCauseMini: FunctionComponent = () => {
  const result = usePageQuery<CauseMiniFragment>(FRAGMENT, {
    notifyOnNetworkStatusChange: true,
  });

  if (result.loading) {
    return (
      <AboutRelationshipMiniSkeleton
        animated
        onRefresh={result.refetch}
        displayRefreshButton={false}
        inline
      />
    );
  }

  if (result.error) {
    captureException(result.error);
    return <AboutRelationshipMiniSkeleton onRefresh={result.refetch} inline />;
  }

  const campaigns = getCampaigns(result.data?.relationships?.parents ?? []);

  if (
    !UsePageQueryResult.hasData(result) ||
    !result.data.relationships ||
    (result.data.relationships.beneficiaries.totalCount === 0 &&
      campaigns.length === 0)
  ) {
    return null;
  }

  const {
    relationships: {
      beneficiaries: { nodes: beneficiaryNodes, totalCount: totalCharityCount },
    },
  } = result.data;

  const charities = filterByTypename(beneficiaryNodes, 'Charity');

  if (campaigns.length > 0 && totalCharityCount === 1) {
    const [{ name, slug: charitySlug, logo: charityLogo }] = charities;
    return (
      <AboutCampaignMini
        totalCount={campaigns.length}
        campaigns={campaigns.map(
          ({ id, title, logo, url, summary, branding }) => ({
            id,
            title,
            logo: logo ?? charityLogo,
            url,
            summary,
            branding: { logo: branding?.logo ?? null },
          }),
        )}
        charityName={name || ''}
        charityShortName={charitySlug}
      />
    );
  }

  if (totalCharityCount > 0) {
    return (
      <AboutCharityMini
        totalCount={totalCharityCount}
        charities={charities.map(charity => ({
          id: charity.id,
          title: charity.name || '',
          logo: charity.logo,
          url: `/${charity.slug}`,
          impactStatement: charity.impactStatement || '',
        }))}
      />
    );
  }

  return null;
};

export default AboutCauseMini;
